import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { candidateAPI, credentialsAPI } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import ComplianceRequestDialog from '../components/ComplianceRequestDialog';
import { PortalNotifications } from '../components/portal/PortalNotifications';
import { ApplicationsList, BrowseJobs } from '../components/portal/PortalTabs';
import { AssessmentsTab, InterviewsTab, CredentialsTab } from '../components/portal/PortalResultTabs';
import { ApplyDialog } from '../components/portal/ApplyDialog';
import { toast } from 'sonner';

export default function CandidatePortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyJob, setApplyJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('applied');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAedt, setShowAedt] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [pRes, jRes, aRes, nRes] = await Promise.all([
        candidateAPI.getProfile(),
        candidateAPI.getJobs(),
        candidateAPI.getApplications(),
        candidateAPI.getNotifications(),
      ]);
      setProfile(pRes.data);
      setJobs(jRes.data);
      setApplications(aRes.data);
      setNotifications(nRes.data);
      try {
        const tRes = await credentialsAPI.getForCandidate(pRes.data.user.email);
        setTokens(tRes.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to load tokens:', err);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh to pick up new notifications/assessments after applying
  useEffect(() => {
    const hasRecentApp = applications.some(a => (Date.now() - new Date(a.applied_at).getTime()) < 60000);
    if (!hasRecentApp) return;
    const iv = setInterval(loadData, 5000);
    return () => clearInterval(iv);
  }, [applications, loadData]);

  const handleApply = async () => {
    if (!selectedFile || !applyJob) {
      toast.error('Please select your resume file');
      return;
    }
    setApplying(true);
    try {
      await candidateAPI.apply(applyJob._id, selectedFile);
      toast.success(`Applied to ${applyJob.title} successfully! An assessment will be generated shortly.`);
      setApplyJob(null);
      setSelectedFile(null);
      setActiveTab('applied');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const markRead = async (id) => {
    try {
      await candidateAPI.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to mark notification read:', err);
    }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>Loading your portal...</div>;

  const availableJobs = jobs.filter(j => !j.has_applied);
  const unreadCount = notifications.filter(n => !n.read).length;
  const assessmentNotifs = notifications.filter(n => n.type === 'assessment_assigned' && n.data?.access_code);
  const hasNewAssessment = assessmentNotifs.filter(n => !n.read).length > 0;

  return (
    <div data-testid="candidate-portal">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Portal</h1>
          <p className="page-subtitle">Welcome, {user?.name}</p>
        </div>
        <PortalNotifications
          notifications={notifications}
          unreadCount={unreadCount}
          show={showNotifications}
          setShow={setShowNotifications}
          markRead={markRead}
        />
      </div>

      {hasNewAssessment && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', borderRadius: 12, background: 'rgba(43,108,176,0.06)', border: '1px solid rgba(43,108,176,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/><path d="M9 14l2 2 4-4"/>
            </svg>
            <div>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>New Assessment Available!</div>
              <div style={{ fontSize: '0.75rem', color: '#64748B' }}>You have a skills assessment to complete. Check the Assessments tab.</div>
            </div>
          </div>
          <Button onClick={() => setActiveTab('assessments')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            View Assessment
          </Button>
        </div>
      )}

      <div className="kpi-grid" style={{ marginBottom: '2rem' }}>
        {[
          { label: 'Applied Jobs', value: applications.length, color: '#0076A3' },
          { label: 'Assessments', value: profile?.total_assessments || 0, color: '#0076A3' },
          { label: 'Interviews', value: profile?.total_interviews || 0, color: '#059669' },
          { label: 'Open Positions', value: availableJobs.length, color: '#D97706' },
        ].map(({ label, value, color }) => (
          <div key={label} className="kpi-tile">
            <div className="kpi-label">{label}</div>
            <div className="kpi-value" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 10, marginBottom: '1.25rem' }}>
          <TabsTrigger value="applied" style={{ borderRadius: 8 }}>My Applications ({applications.length})</TabsTrigger>
          <TabsTrigger value="browse" style={{ borderRadius: 8 }}>Browse Jobs ({availableJobs.length})</TabsTrigger>
          <TabsTrigger value="assessments" style={{ borderRadius: 8 }}>Assessments ({profile?.total_assessments || 0})</TabsTrigger>
          <TabsTrigger value="interviews" style={{ borderRadius: 8 }}>Interviews ({profile?.total_interviews || 0})</TabsTrigger>
          <TabsTrigger value="credentials" style={{ borderRadius: 8 }}>My Credentials ({tokens.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="applied">
          <ApplicationsList applications={applications} onViewDetails={(jobId) => navigate(`/portal/application/${jobId}`)} />
        </TabsContent>

        <TabsContent value="browse">
          <BrowseJobs jobs={availableJobs} onApply={(job) => { setApplyJob(job); setSelectedFile(null); }} />
        </TabsContent>

        <TabsContent value="assessments">
          <AssessmentsTab assessmentNotifs={assessmentNotifs} assessments={profile?.assessments || []} />
        </TabsContent>

        <TabsContent value="interviews">
          <InterviewsTab interviews={profile?.interviews || []} />
        </TabsContent>

        <TabsContent value="credentials">
          <CredentialsTab tokens={tokens} />
        </TabsContent>
      </Tabs>

      <ApplyDialog
        applyJob={applyJob}
        onClose={() => { setApplyJob(null); setSelectedFile(null); }}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        applying={applying}
        onSubmit={handleApply}
        onRequestAedt={() => setShowAedt(true)}
      />

      <ComplianceRequestDialog
        open={showAedt}
        onOpenChange={setShowAedt}
        category="aedt_alternative"
        context="application"
        jobTitle={applyJob?.title}
        defaultName={user?.name || ''}
        defaultEmail={user?.email || ''}
      />
    </div>
  );
}
